import { useEffect, useState, useCallback } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { useSignal } from '@backstage/plugin-signals-react';
import { oneadvancedApiRef } from '../../../api/OneAdvancedApi';
import type { Banner } from '../types';

const DISMISS_KEY = 'oneadvanced:dismissed-banners:v1';

function getDismissed(): Set<string> {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
}

function saveDismissed(keys: Set<string>): void {
  try {
    localStorage.setItem(DISMISS_KEY, JSON.stringify(Array.from(keys)));
  } catch {
    // ignore storage errors
  }
}

// Composite key so re-edited banners reappear after dismissal
function dismissKey(banner: Banner): string {
  return `${banner.id}-${banner.updatedAt}`;
}

interface UseActiveBannersResult {
  banners: Banner[];
  loading: boolean;
  error: Error | undefined;
  dismiss: (banner: Banner) => void;
}

export function useActiveBanners(): UseActiveBannersResult {
  const api = useApi(oneadvancedApiRef);
  const [allBanners, setAllBanners] = useState<Banner[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(getDismissed);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>(undefined);

  const fetchBanners = useCallback(async () => {
    try {
      const data = await api.banners.getActiveBanners();
      setAllBanners(data);
      setError(undefined);
    } catch (e) {
      setError(e as Error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Initial fetch
  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  // Real-time updates via Backstage Signals (WebSocket)
  const { lastSignal } = useSignal<{ action: string }>('oneadvanced:banners-updated');
  useEffect(() => {
    if (lastSignal) {
      fetchBanners();
    }
  }, [lastSignal, fetchBanners]);

  // Client-side expiry timer: find the soonest expires_at among active banners
  // and schedule a re-fetch at exactly that moment so the banner disappears
  // without waiting for a signal or page refresh.
  useEffect(() => {
    const now = Date.now();
    const futureTimes = allBanners
      .filter(b => b.expiresAt)
      .map(b => new Date(b.expiresAt!).getTime())
      .filter(t => t > now);

    if (futureTimes.length === 0) return undefined;

    const next = Math.min(...futureTimes);
    const delay = next - now;
    const timer = setTimeout(() => {
      fetchBanners();
    }, delay);

    return () => clearTimeout(timer);
  }, [allBanners, fetchBanners]);

  const dismiss = useCallback((banner: Banner) => {
    setDismissed(prev => {
      const next = new Set(prev);
      next.add(dismissKey(banner));
      saveDismissed(next);
      return next;
    });
  }, []);

  const banners = allBanners.filter(b => !dismissed.has(dismissKey(b)));

  return { banners, loading, error, dismiss };
}
